import { useState } from 'react';

const guarantees = [
  { icon: 'ri-shield-check-line', title: 'ISO-9001 Certified', desc: 'Quality management systems certified to international standards' },
  { icon: 'ri-award-line', title: 'SGS Verified', desc: 'Products verified by SGS inspection and testing' },
  { icon: 'ri-medal-line', title: 'CE Compliant', desc: 'European conformity standards met across product lines' },
  { icon: 'ri-verified-badge-line', title: 'TUV Approved', desc: 'Technical safety and quality approved by TUV' },
  { icon: 'ri-ship-line', title: 'FOB Shipping', desc: 'Flexible FOB terms from major Chinese ports' },
  { icon: 'ri-bank-card-line', title: 'L/C & TT Accepted', desc: 'We accept Letter of Credit and bank wire transfers' },
  { icon: 'ri-time-line', title: 'Fast Dispatch', desc: 'Products dispatched quickly after deposit confirmation' },
  { icon: 'ri-global-line', title: '20+ Countries Served', desc: 'Trusted by buyers across 20+ countries worldwide' },
  { icon: 'ri-customer-service-2-line', title: 'Dedicated Support', desc: 'Personal account managers for every client' },
  { icon: 'ri-search-eye-line', title: 'Mill Inspection', desc: 'We inspect at the mill before shipment on your behalf' },
];

const certDocs = [
  {
    id: 'sgs',
    label: 'SGS Test Report',
    tag: 'SGS',
    img: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/c6a7b225-8e65-4e74-ad7b-90d3be9afb5b_7c300fa317ff4b335e4e156df2573ae1c7f7d67d.jpg?v=aab2f024a698be9b041c78256bfad26e',
  },
  {
    id: 'cert2',
    label: 'Quality Certificate',
    tag: 'CERT',
    img: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/03d7badf-7949-4984-af22-5021dccdcb59_b1461630e4b1eef8ff03ee84b0e216ebe153f4e2.png?v=2a60f433d2e88921e7335332e5ce74bb',
  },
  {
    id: 'cert3',
    label: 'Inspection Certificate',
    tag: 'INSP',
    img: 'https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/5efabed0-1ce4-4f7f-bca9-a64ea400c0d7_bfa5e8488ca42eabd80c13c30f0bf7443097c517.jpeg?v=10f6b25aa383f007e2a04db9a4c90816',
  },
];

export default function QualityGuarantee() {
  const [lightbox, setLightbox] = useState<string | null>(null);

  return (
    <section className="py-16 bg-gray-900">
      <div className="max-w-[1420px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-10 items-start">
          {/* Left text */}
          <div className="lg:w-2/5 text-center lg:text-left">
            <span className="text-xs font-bold tracking-widest uppercase mb-2 block" style={{ color: '#7eb8f7' }}>Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">We Guarantee</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              We hold ourselves to the highest standards so you can buy with confidence, every time.
            </p>

            {/* Real cert document previews */}
            <div className="mt-6">
              <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-3">Verified Documents</p>
              <div className="flex gap-3 flex-wrap justify-center lg:justify-start">
                {certDocs.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setLightbox(doc.img)}
                    className="group relative w-[90px] h-[120px] rounded-lg overflow-hidden border border-gray-700 hover:border-gray-400 transition-all cursor-pointer flex-shrink-0"
                    title={doc.label}
                  >
                    {/* Blurred preview */}
                    <img
                      src={doc.img}
                      alt={doc.label}
                      className="w-full h-full object-cover object-top"
                      style={{ filter: 'blur(2px)', transform: 'scale(1.05)' }}
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors flex flex-col items-center justify-end pb-3 px-1">
                      <span className="text-white text-[10px] font-black tracking-wide text-center leading-tight">{doc.label}</span>
                    </div>
                    {/* View icon */}
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-white/20 group-hover:bg-white/40 transition-colors">
                      <i className="ri-eye-line text-white text-[10px]" />
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-600 mt-3 italic">Click to preview — full documents available on request</p>
            </div>
          </div>

          {/* Grid */}
          <div className="lg:w-3/5 w-full">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {guarantees.map((item) => (
                <div
                  key={item.title}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full transition-colors" style={{ backgroundColor: 'rgba(30,95,168,0.15)' }}>
                    <i className={`${item.icon} text-xl`} style={{ color: '#7eb8f7' }} />
                  </div>
                  <span className="text-xs text-gray-300 text-center font-semibold leading-tight">{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-w-2xl w-full bg-white rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200" style={{ backgroundColor: '#0a2342' }}>
              <span className="text-white text-sm font-bold">Certificate Preview</span>
              <button
                onClick={() => setLightbox(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white text-lg" />
              </button>
            </div>
            {/* Image — top portion only, no sensitive details */}
            <div className="w-full overflow-hidden" style={{ maxHeight: '480px' }}>
              <img
                src={lightbox}
                alt="Certificate"
                className="w-full object-cover object-top"
                style={{ maxHeight: '480px' }}
              />
            </div>
            {/* Footer note */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-500">Full document available upon request. Contact us via WhatsApp or email.</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
